import { Suspense, lazy, useRef, useState, useMemo } from 'react';
import { useGameStateStore, useUnitSelectionStore, useBuildingSelectionStore } from '@shared/stores';
import MainMenu from '@ui/MainMenu';
import SettingsMenu from '@ui/SettingsMenu';
import InGameSettingsMenu from '@ui/InGameSettingsMenu';
import DevAdminPanel from '@ui/DevAdminPanel';
import LoadingScreen from '@ui/LoadingScreen';
import GameOverScreen from '@ui/GameOverScreen';
import DebugCoordinateDisplay from '@ui/DebugCoordinateDisplay';
import ResourceDisplay from '@ui/ResourceDisplay';
import PauseMenu from '@ui/PauseMenu';
import BottomPanel from '@ui/BottomPanel';
import Minimap from '@ui/Minimap';
import UnitSelectionDisplay from '@ui/UnitSelectionDisplay';
import BuildingSelectionDisplay from '@ui/BuildingSelectionDisplay';
import UnitActionsMenu from '@ui/UnitActionsMenu';
import BuildingActionsPanel from '@ui/BuildingActionsPanel';

// Lazy-load DevMenu component
const DevMenu = lazy(() => import('@ui/DevMenu'));
// Lazy-load MapMakerScreen component
const MapMakerScreen = lazy(() => import('@ui/MapMakerScreen'));
import { useGameTooltip } from './hooks/useGameTooltip';
import { usePhaserGame } from './hooks/usePhaserGame';
import { useBuildingPlacement } from './hooks/useBuildingPlacement';
import { useBuildingPlacementClick } from './hooks/useBuildingPlacementClick';
import { useBuildingKeyboardShortcuts } from './hooks/useBuildingKeyboardShortcuts';
import { useGamePause } from './hooks/useGamePause';
import { useUnitActionKeyboardShortcuts } from './hooks/useUnitActionKeyboardShortcuts';
import { useUnitSelection } from './hooks/useUnitSelection';
import { useBuildingSelection } from './hooks/useBuildingSelection';
import { useBuildingActionKeyboardShortcuts } from './hooks/useBuildingActionKeyboardShortcuts';

/**
 * Combined selection display that shows either units or buildings.
 */
function SelectionDisplay({ scene }: { scene: unknown | null }) {
  const selectedBuildingId = useBuildingSelectionStore((state) => state.selectedBuildingId);

  // Show building selection if a building is selected
  if (selectedBuildingId) {
    return <BuildingSelectionDisplay scene={scene} />;
  }

  // Otherwise show unit selection (or "no units selected" message)
  return <UnitSelectionDisplay scene={scene} />;
}

/**
 * Combined actions display that shows either unit actions or building actions.
 */
function ActionsDisplay({ scene }: { scene: unknown | null }) {
  const selectedUnitIds = useUnitSelectionStore((state) => state.selectedUnitIds);
  const selectedBuildingId = useBuildingSelectionStore((state) => state.selectedBuildingId);

  // Show building actions if a building is selected
  if (selectedBuildingId) {
    return <BuildingActionsPanel scene={scene} />;
  }

  // Show unit actions if units are selected
  if (selectedUnitIds.length > 0) {
    return <UnitActionsMenu scene={scene} />;
  }

  return null;
}

function App() {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const currentScreen = useGameStateStore((state) => state.currentScreen);
  const isGameOver = useGameStateStore((state) => state.isGameOver);
  const isPaused = useGameStateStore((state) => state.isPaused);
  const showInGameSettings = useGameStateStore((state) => state.showInGameSettings);
  const [gameScene, setGameScene] = useState<unknown | null>(null);
  const { setupTooltipCallback, hoveredTile } = useGameTooltip(gameContainerRef);

  // Memoize the options to prevent effect re-runs
  const phaserGameOptions = useMemo(
    () => ({
      onSceneReady: (scene: unknown) => {
        setGameScene(scene);
        setupTooltipCallback(scene);
      },
    }),
    [setupTooltipCallback]
  );

  const { isGameReady } = usePhaserGame(gameContainerRef, phaserGameOptions);

  // Connect building placement preview
  useBuildingPlacement({ scene: gameScene, hoveredTile });

  // Connect tile clicks to building placement
  useBuildingPlacementClick({ scene: gameScene });

  // Set up keyboard shortcuts for building placement & build menu
  useBuildingKeyboardShortcuts({ scene: gameScene });

  // Connect game pause/resume with Phaser scene
  useGamePause({ scene: gameScene });

  // Connect unit selection to store
  useUnitSelection({ scene: gameScene });

  // Connect building selection to store
  useBuildingSelection({ scene: gameScene });

  // Set up keyboard shortcuts for unit actions
  useUnitActionKeyboardShortcuts({ scene: gameScene });

  // Set up keyboard shortcuts for building actions
  useBuildingActionKeyboardShortcuts({ scene: gameScene });

  // Show loading screen until game is fully ready
  const shouldShowLoading =
    currentScreen === 'loading' || (currentScreen === 'game' && !isGameReady);

  // Conditionally render based on game state
  if (currentScreen === 'menu') {
    return <MainMenu />;
  }

  if (currentScreen === 'settings') {
    return <SettingsMenu scene={gameScene} />;
  }

  if (currentScreen === 'devAdmin') {
    return <DevAdminPanel />;
  }

  if (currentScreen === 'devMenu') {
    return (
      <Suspense
        fallback={
          <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
            Loading...
          </div>
        }
      >
        <DevMenu />
      </Suspense>
    );
  }

  if (currentScreen === 'mapMaker') {
    return (
      <Suspense
        fallback={
          <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
            Loading...
          </div>
        }
      >
        <MapMakerScreen />
      </Suspense>
    );
  }

  // Always render the game container when not in menu, so the ref can be attached
  // Overlay the loading screen on top if needed
  return (
    <div className="w-screen h-screen bg-gray-900 flex flex-col relative">
      {/* Top bar - Currency and Menu button */}
      {!shouldShowLoading && !isGameOver && <ResourceDisplay />}

      {/* Game over screen overlay - highest priority */}
      {(isGameOver || currentScreen === 'gameOver') && (
        <div className="absolute inset-0 z-50">
          <GameOverScreen />
        </div>
      )}

      {/* Loading screen overlay - shown when loading or game not ready */}
      {!isGameOver && shouldShowLoading && (
        <div className="absolute inset-0 z-50">
          <LoadingScreen />
        </div>
      )}

      {/* Middle area - Game canvas container */}
      <div className="flex-1 relative overflow-hidden min-h-0">
        <div ref={gameContainerRef} id="game-container" className="w-full h-full" />

        {/* Game UI overlays - only show when game is ready and not game over */}
        {!shouldShowLoading && !isGameOver && (
          <>
            <PauseMenu />
            {/* In-game settings overlay - shows when paused and settings requested */}
            {isPaused && showInGameSettings && <InGameSettingsMenu scene={gameScene} />}
            <DebugCoordinateDisplay scene={gameScene} />
          </>
        )}
      </div>

      {/* Bottom panel - Minimap, Unit/Building Selection, and Actions */}
      {!shouldShowLoading && !isGameOver && (
        <BottomPanel
          minimap={<Minimap scene={gameScene} />}
          unitSelection={
            <SelectionDisplay scene={gameScene} />
          }
          actions={<ActionsDisplay scene={gameScene} />}
        />
      )}
    </div>
  );
}

export default App;
